import { create } from 'zustand';
import type { Session } from '@supabase/supabase-js';
import type { UserProfile, UserRole } from '@/shared/types/common.types';
import { supabase } from '@/shared/services/supabaseClient';
import { queryClient } from '@/shared/config/queryClient';

interface AuthState {
  user: UserProfile | null;
  session: Session | null;
  organizationId: string | null;
  role: UserRole | null;
  isLoading: boolean;

  setUser: (user: UserProfile | null) => void;
  setSession: (session: Session | null) => void;
  setOrganizationId: (id: string | null) => void;
  setRole: (role: UserRole | null) => void;
  setIsLoading: (loading: boolean) => void;
  signOut: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  session: null,
  organizationId: null,
  role: null,
  isLoading: true,

  setUser: (user) => { set({ user }); },
  setSession: (session) => { set({ session }); },
  setOrganizationId: (organizationId) => { set({ organizationId }); },
  setRole: (role) => { set({ role }); },
  setIsLoading: (isLoading) => { set({ isLoading }); },

  signOut: async () => {
    // Sign out from Supabase (clears JWT from localStorage)
    await supabase.auth.signOut();

    // Clear auth store
    set({
      user: null,
      session: null,
      organizationId: null,
      role: null,
      isLoading: false,
    });

    // Clear other stores
    useOrgStore.getState().reset();
    useUIStore.getState().reset();
    useOnboardStore.getState().reset();

    // Clear TanStack Query cache
    queryClient.clear();

    // Clear browser storage
    localStorage.clear();
    sessionStorage.clear();
  },
}));

// Lazy imports to avoid circular dependency — resolved at runtime via getState()
import { useOrgStore } from './orgStore';
import { useUIStore } from './uiStore';
import { useOnboardStore } from './onboardStore';
